import React, { useMemo } from "react";
import PathwayFogTeaser from "./PathwayFogTeaser";

function hash(text = "") {
  let value = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 16777619);
  }
  return Math.abs(value >>> 0);
}

function terrainNodes(groups) {
  const slots = [
    [120, 100],
    [250, 74],
    [584, 96],
    [690, 178],
    [610, 300],
    [455, 326],
    [252, 316],
    [104, 252],
    [354, 112],
    [530, 224],
    [310, 236],
    [704, 82],
  ];

  return groups.slice(0, 12).map((group, index) => {
    const h = hash(group.key || group.label || String(index));
    const [baseX, baseY] = slots[index % slots.length];

    return {
      ...group,
      x: baseX + ((h % 23) - 11),
      y: baseY + ((Math.floor(h / 31) % 23) - 11),
      bendX: 400 + ((Math.floor(h / 7) % 170) - 85),
      bendY: 205 + ((Math.floor(h / 17) % 130) - 65),
      size: 34 + Math.min(26, (group.pathwayCount || 1) * 4),
      seed: h,
    };
  });
}

function Feature({ node, focused }) {
  const orientation = node.orientation || "unresolved";
  const x = node.x;
  const y = node.y;

  if (orientation === "constrains") {
    return (
      <g className={`rr-terrain-feature constrains ${focused ? "focused" : ""}`}>
        <path
          d={`M ${x - 27} ${y + 18} L ${x - 8} ${y - 22} L ${x + 4} ${y + 5} L ${x + 18} ${y - 16} L ${x + 29} ${y + 18}`}
          className="ridge"
        />
        <path
          d={`M ${x - 23} ${y + 24} Q ${x} ${y + 8} ${x + 26} ${y + 24}`}
          className="friction"
        />
      </g>
    );
  }

  if (orientation === "mixed") {
    return (
      <g className={`rr-terrain-feature mixed ${focused ? "focused" : ""}`}>
        <path
          d={`M ${x - 28} ${y + 24} Q ${x - 7} ${y} ${x} ${y - 7} Q ${x + 8} ${y + 3} ${x + 29} ${y + 22}`}
          className="fork"
        />
        <path
          d={`M ${x} ${y - 7} Q ${x + 2} ${y - 28} ${x + 25} ${y - 33}`}
          className="fork alt"
        />
      </g>
    );
  }

  if (orientation !== "affords") {
    return (
      <g className={`rr-terrain-feature unresolved ${focused ? "focused" : ""}`}>
        <ellipse cx={x} cy={y} rx="36" ry="22" className="mist" />
        <text x={x} y={y + 4} textAnchor="middle">?</text>
      </g>
    );
  }

  return (
    <g className={`rr-terrain-feature affords ${focused ? "focused" : ""}`}>
      <ellipse cx={x} cy={y} rx="31" ry="21" className="glade" />
      <path
        d={`M ${x - 24} ${y + 10} Q ${x - 2} ${y - 18} ${x + 24} ${y + 8}`}
        className="corridor"
      />
    </g>
  );
}

function TerrainRoute({ node, focused, onFocus, onBlur, onInspect }) {
  return (
    <g
      className={`rr-terrain-route-v11 ${node.orientation || "unresolved"} ${
        focused ? "focused" : ""
      }`}
      role="button"
      tabIndex="0"
      onMouseEnter={() => onFocus?.(node.key)}
      onMouseLeave={() => onBlur?.()}
      onFocus={() => onFocus?.(node.key)}
      onBlur={() => onBlur?.()}
      onClick={() => onInspect?.(node)}
    >
      <path
        d={`M 400 205 Q ${node.bendX} ${node.bendY}, ${node.x} ${node.y}`}
        className="route"
      />
      <circle cx={node.x} cy={node.y} r={focused ? 8 : 5} className="node" />
      <circle cx={node.x} cy={node.y} r={focused ? 22 : 15} className="focus-ring" />
    </g>
  );
}

export default function TerrainFieldMap({
  capabilityLabel,
  groups,
  focusKey,
  onFocus,
  onBlur,
  onInspect,
}) {
  const nodes = useMemo(() => terrainNodes(groups), [groups]);

  const active =
    groups.find((group) => group.key === focusKey) ||
    null;

  return (
    <section className="rr-terrain-map-card rr-terrain-map-v11">
      <header>
        <div>
          <span className="v25a-eyebrow">RELATIONAL TERRAIN</span>
          <h3>The same pathways, rendered as terrain features</h3>
        </div>

        <div className="rr-terrain-header-badges">
          <span className="rr-linked-view-badge">linked to radial</span>
          <span className="rr-terrain-metaphor-badge">METAPHOR · NOT GEOGRAPHY</span>
        </div>
      </header>

      <p className="rr-terrain-map-intro">
        The terrain is deliberately mixed: viable routes, friction ridges,
        forks and fog can sit beside one another. No side of the map means
        “good” or “bad”.
      </p>

      <div className="rr-terrain-map">
        <svg viewBox="0 0 800 410" role="img" aria-label="Relational terrain map">
          <defs>
            <radialGradient id="rrV11Base">
              <stop offset="0%" stopColor="#f3f7f1" />
              <stop offset="52%" stopColor="#f6f4ed" />
              <stop offset="100%" stopColor="#eeeae2" />
            </radialGradient>

            <filter id="rrV11Fog">
              <feGaussianBlur stdDeviation="11" />
            </filter>

            <filter id="rrV11SoftGlow">
              <feGaussianBlur stdDeviation="5" />
            </filter>
          </defs>

          <rect width="800" height="410" rx="24" fill="url(#rrV11Base)" />

          <path
            d="M0 305 C108 258 171 322 258 287 S388 218 472 237 S622 339 800 270 L800 410 L0 410 Z"
            className="rr-v11-lowland"
          />
          <path
            d="M0 127 C105 80 164 129 256 105 S427 45 541 90 S686 143 800 103"
            className="rr-v11-ridge-band"
          />
          <path
            d="M44 382 C172 331 257 352 323 286 S381 209 456 173 S588 108 742 28"
            className="rr-v11-river"
          />
          <path
            d="M15 258 C115 220 179 245 255 211 S417 121 512 151 S651 245 785 211"
            className="rr-v11-contour"
          />
          <path
            d="M4 177 C104 134 202 169 304 132 S484 81 612 122 S712 164 799 150"
            className="rr-v11-contour subtle"
          />

          {nodes.map((node) => {
            const focused = node.key === focusKey;
            return (
              <g key={`${node.key}-terrain`}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.size}
                  className={`rr-v11-zone ${node.orientation || "unresolved"} ${
                    focused ? "focused" : ""
                  }`}
                  filter="url(#rrV11Fog)"
                />

                <Feature node={node} focused={focused} />

                <TerrainRoute
                  node={node}
                  focused={focused}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  onInspect={onInspect}
                />
              </g>
            );
          })}

          <circle cx="400" cy="205" r="59" className="rr-v11-core-halo" />
          <circle cx="400" cy="205" r="31" className="rr-v11-core" />
          <text x="400" y="199" textAnchor="middle" className="rr-v11-core-label">
            SAME
          </text>
          <text x="400" y="212" textAnchor="middle" className="rr-v11-core-label">
            CAPABILITY
          </text>
        </svg>

        <div className="rr-terrain-caption rr-terrain-caption-v11">
          <div>
            <b>{capabilityLabel}</b>
            <span>stable center · mixed relational topology</span>
          </div>

          <div className="rr-terrain-feature-key">
            <span className="glade">glade</span>
            <span className="ridge">ridge</span>
            <span className="fork">fork</span>
            <span className="fog">fog</span>
          </div>
        </div>
      </div>

      {active ? (
        <PathwayFogTeaser
          group={active}
          compact
          onInspect={onInspect}
        />
      ) : (
        <div className="rr-v12-terrain-idle">
          <span className="v25a-eyebrow">WHOLE TERRAIN</span>
          <p>
            No pathway is selected. Hover a route here or a glade in the radial
            overview to connect the two representations.
          </p>
        </div>
      )}

      <small className="rr-terrain-map-note">
        Feature position is illustrative. Route identity, orientation, root
        count and pathway count remain analytical receipts; spatial position
        itself is not one.
      </small>
    </section>
  );
}
